import React, { useCallback, useRef, useState, useEffect } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import {
  X,
  Eye,
  Image as ImageIcon,
  Upload,
  AlertCircle,
  Trash2,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import SectionHeader from "../../components/ui/SectionHeader";
import { useStep } from "../../context/StepContext";
import { useSectionContext, SectionKey } from "../../context/SectionContext";
import { getNextSectionRoute } from "../../lib/getNextSectionRoute";
import { uploadGalleryImages, getWebsiteSettings, deleteGalleryImage, GalleryImage } from "../../services/websitesetting";
import { validateImage } from "../../lib/image";
import { resizeImage } from "../../lib/image";
import { useAuth } from "../../auth/AuthContext";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";


interface PreviewImage {
  file: File;
  error?: string;
}

const GallerySection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sections, markSectionComplete, completions } = useSectionContext();
  const { currentStep, setCurrentStep } = useStep();
  const toast = useToast();
  const [images, setImages] = useState<PreviewImage[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<number | null>(null);
  const { subdomain } = useOutletContext<{ subdomain: string }>();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => setCurrentStep(5), [setCurrentStep]);

  useEffect(() => {
    const fetchGallery = async () => {
      setIsLoading(true);
      try {
        const data = await getWebsiteSettings();
        if (data && Array.isArray(data.galleries)) {
          const mappedGalleries = data.galleries.map((g: any) => ({
            ...g,
            image: g.image.startsWith('http') ? g.image : `https://salonify.co.uk/user-uploads/gallery/${g.image}`
          }));

          setGalleryImages(mappedGalleries);
        }
      } catch (err: any) {
        console.error("fetchGallery ERROR:", err);
        console.error("fetchGallery Response:", err.response);
        console.error("fetchGallery Response Data:", err.response?.data);
        const status = err.response?.status;
        if (status === 404) {
          toast.error("Gallery not found. You can start adding images.");
        } else {
          toast.error("Failed to load gallery. Please refresh.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // ---------------- Image Handlers ----------------
  const handleFilesUpload = async (files: FileList | null) => {
    if (!files) return;
    setError(null);

    const newImages: PreviewImage[] = await Promise.all(
      Array.from(files).map(async (file) => {
        try {
          await validateImage(file);
          return { file };
        } catch (err: any) {
          toast.error(`${file.name}: ${err.message}`);
          return { file, error: err.message };
        }
      })
    );

    setImages((prev) => [...prev, ...newImages]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFilesUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) =>
    e.preventDefault();

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteGalleryImage = async (imageId: number) => {
    setImageToDelete(imageId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!imageToDelete) return;

    setIsUploading(true);
    try {
      await deleteGalleryImage(imageToDelete);
      setGalleryImages(prev => prev.filter(img => img.id !== imageToDelete));
      toast.success("Image deleted successfully!");
    } catch (err: any) {
      console.error("handleDeleteGalleryImage ERROR:", err);
      console.error("handleDeleteGalleryImage Response:", err.response);
      console.error("handleDeleteGalleryImage Response Data:", err.response?.data);

      const status = err.response?.status;
      let userMessage = "Failed to delete image.";

      if (status === 404) {
        userMessage = "Image not found. It may have been already deleted.";
      } else if (status === 403) {
        userMessage = "You don't have permission to delete this image.";
      } else if (status === 500) {
        userMessage = "Server error occurred. Please try again.";
      } else if (err.message === "Network Error") {
        userMessage = "Connection lost. Please check your internet.";
      }

      setError(userMessage);
      toast.error(userMessage);
    } finally {
      setIsUploading(false);
      setImageToDelete(null);
    }
  };

  const handleContinue = useCallback(async () => {
    if (images.length === 0) {
      if (galleryImages.length > 0) {
        markSectionComplete("gallerysection" as SectionKey, true);
        toast.success("Changes saved successfully!");
        return;
      } else {
        setError("Please upload at least one image before continuing.");
        return;
      }
    }
    const invalidImages = images.filter((img) => img.error);
    if (invalidImages.length > 0) {
      setError(
        "Please fix or remove the unsupported images before continuing."
      );
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const filesToUpload = await Promise.all(
        images.map(async (img) => {
          let file = img.file;
          if (file.size > 1.5 * 1024 * 1024) {
            const resized = await resizeImage(file, 1600, 0.8);
            file = new File([resized], file.name, { type: resized.type });
          }
          return file;
        })
      );

      await uploadGalleryImages(filesToUpload);
      markSectionComplete("gallerysection" as SectionKey, true);
      toast.success("Gallery updated successfully!");
      setImages([]); // Clear preview images

    } catch (err: any) {
      console.error("handleContinue ERROR:", err);
      console.error("handleContinue Response:", err.response);
      console.error("handleContinue Response Data:", err.response?.data);

      const status = err.response?.status;
      let userMessage = "Failed to upload images. Please try again.";

      if (status === 404) {
        userMessage = "Gallery service not found. Please refresh and try again.";
      } else if (status === 403) {
        userMessage = "You don't have permission to upload to the gallery.";
      } else if (status === 413) {
        userMessage = "One or more images are too large for the server.";
      } else if (status === 422) {
        userMessage = "Invalid image format. Please use JPG or PNG files.";
      } else if (status === 500) {
        userMessage = "Server error while uploading. Please try later.";
      } else if (err.message === "Network Error") {
        userMessage = "Connection lost. Please check your internet and try again.";
      }

      toast.error(userMessage);
      setError(userMessage);
    } finally {
      setIsUploading(false);
    }
  }, [images, markSectionComplete, completions, navigate, sections, galleryImages]);

  return (
    <>
      {(isLoading || isUploading) && (
        <GlobalLoader
          initialPageLoad={isLoading}
          fullScreen={isUploading}
          description={isUploading ? "Updating gallery images..." : "Loading gallery images..."}
        />
      )}
      <div className="space-y-5">

        {/* Header Card */}
        <PageHeader
          title="Gallery Section"
          description="Showcase your best work with a beautiful image gallery"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={
            <ViewWebsiteButton subdomain={subdomain} />
          }
        />

        {/* Main Content */}
        <WebsiteSectionCard
          title="Manage Gallery"
          icon={ImageIcon}
        >
          <div className="space-y-6">
            <div>
              <SectionHeader title="Upload New Images" />

              <InfoContainer
                message="Upload photos to showcase your business. You can select multiple images at once."
                iconSize={13}
                className="mb-4"
              />

              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all bg-gray-50 hover:bg-white min-h-[200px] flex flex-col justify-center items-center group ${error
                  ? "border-red-300 bg-red-50/10"
                  : "border-gray-300 hover:border-[#885ABB]"
                  }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Upload className="text-[#885ABB]" size={24} />
                </div>
                <p className="text-gray-900 font-medium mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 max-w-xs">
                  SVG, PNG, JPG or GIF (max 1.5MB per image)
                </p>

                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFilesUpload(e.target.files)}
                  ref={fileInputRef}
                  className="hidden"
                />
              </div>

              {error && (
                <div className="mt-3 flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-100">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </div>

            {images.length > 0 && (
              <div>
                <SectionHeader title={`Ready to Upload (${images.length})`} />

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {images.map((img, idx) => (
                    <div
                      key={idx}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shadow-sm hover:shadow-md transition-all"
                    >
                      <img
                        src={URL.createObjectURL(img.file)}
                        alt={`Gallery ${idx}`}
                        className={`w-full h-full object-cover transition-opacity ${img.error ? "opacity-50" : "group-hover:opacity-90"
                          }`}
                      />

                      {/* Error Overlay */}
                      {img.error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 p-2 text-center">
                          <p className="text-red-500 text-xs font-medium">
                            {img.error}
                          </p>
                        </div>
                      )}

                      {/* Remove Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(idx);
                        }}
                        className="absolute top-2 right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110 z-10"
                        title="Remove image"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Gallery Images */}
            {galleryImages.length > 0 && (
              <div className="pt-6 border-t border-gray-100">
                <SectionHeader title={`Existing Gallery Images (${galleryImages.length})`} />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                  {galleryImages.map((img) => (
                    <div
                      key={img.id}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shadow-sm hover:shadow-md transition-all"
                    >
                      <img
                        src={img.image}
                        alt={`Gallery ${img.id}`}
                        className="w-full h-full object-cover transition-opacity group-hover:opacity-90"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGalleryImage(img.id);
                        }}
                        className="absolute top-2 right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110 z-10"
                        title="Delete image"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <Button
                onClick={handleContinue}
                disabled={isUploading || (images.length === 0 && galleryImages.length === 0)}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isUploading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </WebsiteSectionCard>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setImageToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default React.memo(GallerySection);
